/**
 * Change Password Page Component
 * Allows authenticated users to change their password
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  ArrowBack,
  CheckCircle,
} from '@mui/icons-material';
import { authService } from '../../services/authService';
import { useAuth } from '../../stores/authStore';
import { useSnackbar } from 'notistack';
import { PageShell } from '../../components/common/PageShell';

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.currentPassword) {
      errors.currentPassword = 'Senha atual é obrigatória';
    }

    if (!formData.newPassword) {
      errors.newPassword = 'Nova senha é obrigatória';
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = 'A senha deve ter pelo menos 8 caracteres';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      errors.newPassword = 'A senha deve conter letras maiúsculas, minúsculas e números';
    } else if (formData.newPassword === formData.currentPassword) {
      errors.newPassword = 'A nova senha deve ser diferente da senha atual';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await authService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      setSuccess(true);
      enqueueSnackbar('Senha alterada com sucesso! Faça login novamente.', { variant: 'success' });

      // Logout after 2 seconds
      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.message ?? 'Erro ao alterar senha';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (error) {
      setError(null);
    }
  };

  const breadcrumbItems = [
    { label: 'Início', path: '/' },
    { label: 'Alterar Senha' },
  ];

  return (
    <PageShell
      breadcrumbs={breadcrumbItems}
      title="Alterar Senha"
      subtitle="Digite sua senha atual e a nova senha"
    >
        <Card sx={{ maxWidth: 'sm', mx: 'auto' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {success ? (
              // Success state
              (<Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircle
                  sx={{
                    fontSize: 64,
                    color: 'success.main',
                    mb: 2,
                  }}
                />
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  Senha Alterada!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Sua senha foi alterada com sucesso.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Você será redirecionado para o login...
                </Typography>
                <CircularProgress size={24} sx={{ mt: 2 }} />
              </Box>)
            ) : (
              // Form state
              (<>
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <TextField
                    fullWidth
                    id="currentPassword"
                    name="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    label="Senha Atual"
                    value={formData.currentPassword}
                    onChange={handleInputChange('currentPassword')}
                    error={!!formErrors.currentPassword}
                    helperText={formErrors.currentPassword}
                    disabled={loading}
                    autoComplete="current-password"
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            edge="end"
                            disabled={loading}
                          >
                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    label="Nova Senha"
                    value={formData.newPassword}
                    onChange={handleInputChange('newPassword')}
                    error={!!formErrors.newPassword}
                    helperText={
                      formErrors.newPassword ||
                      'Mínimo 8 caracteres com letras maiúsculas, minúsculas e números'
                    }
                    disabled={loading}
                    autoComplete="new-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                            disabled={loading}
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirmar Nova Senha"
                    value={formData.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    error={!!formErrors.confirmPassword}
                    helperText={formErrors.confirmPassword}
                    disabled={loading}
                    autoComplete="new-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            disabled={loading}
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      mb: 2,
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Alterar Senha'}
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    startIcon={<ArrowBack />}
                    disabled={loading}
                    onClick={() => navigate(-1)}
                  >
                    Voltar
                  </Button>
                </Box>
              </>)
            )}
          </CardContent>
        </Card>
    </PageShell>
  );
};

export default ChangePasswordPage;
